import React, { useState } from "react";
import { TrackBase } from "../Types";
import { getLaneDistance, formatTimeStr, timeStateToSeconds, formatPace } from "../utils/TimeUtils";
import { Card } from "../ui/Card";
import { RotateCw, Calculator, Timer, Flag } from "lucide-react";

// Used lighter/paler colors for the preset indicators
const THEME_PRESETS = [
    { id: "RED", label: "經典紅", color: "bg-[#e08e79]" },
    { id: "BLUE", label: "北田藍", color: "bg-[#7eb6e6]" },
    {
        id: "RAINBOW",
        label: "夢想彩虹",
        color: "bg-gradient-to-r from-red-300 via-green-300 to-blue-300",
    },
    { id: "PARIS", label: "巴黎奧運紫", color: "bg-[#cbb6e3]" },
];

export const TrackCalculator = () => {
    const [trackBase, setTrackBase] = useState(TrackBase.M400);

    // Calculation Mode
    const [calcMode, setCalcMode] = useState("PACE");

    // Mode 1: Direct Pace
    const [targetPaceMin, setTargetPaceMin] = useState(5);
    const [targetPaceSec, setTargetPaceSec] = useState(0);

    // Mode 2: Distance & Time
    const [totalDistKm, setTotalDistKm] = useState(5);
    const [totalTime, setTotalTime] = useState({ hours: 0, minutes: 25, seconds: 0 });

    // Theme State
    const [trackTheme, setTrackTheme] = useState("RED");

    // Calculate Pace based on mode
    let paceTotalSeconds = 0;
    if (calcMode === "PACE") {
        paceTotalSeconds = targetPaceMin * 60 + targetPaceSec;
    } else {
        const totalTimeSeconds = timeStateToSeconds(totalTime);
        if (totalDistKm > 0) {
            paceTotalSeconds = totalTimeSeconds / totalDistKm;
        }
    }

    // Target Speed in m/s
    const speedMetersPerSecond = paceTotalSeconds > 0 ? 1000 / paceTotalSeconds : 0;

    // Render lanes 1 to 8 (User preference: Lane 1 at top)
    const lanes = [1, 2, 3, 4, 5, 6, 7, 8];

    const handleTotalTimeChange = (field, value) => {
        const num = parseInt(value) || 0;
        setTotalTime((prev) => ({ ...prev, [field]: num }));
    };

    const getLaneStyle = (lane) => {
        let bgClass = "";
        let textClass = "text-gray-800"; // Darker text for lighter backgrounds
        const borderClass = "border-b border-white/60"; // Softer lane lines

        // Using lighter/paler colors as requested
        switch (trackTheme) {
            case "RED":
                bgClass = "bg-[#edaf9e]"; // Lighter Clay
                break;
            case "BLUE":
                bgClass = "bg-[#9ccbf0]"; // Lighter Blue
                break;
            case "PARIS":
                // Alternating very light purple shades
                bgClass = lane % 2 === 0 ? "bg-[#dcd0ed]" : "bg-[#cebee3]";
                break;
            case "RAINBOW":
                // Pastel rainbow colors
                const colors = [
                    "bg-red-200",
                    "bg-orange-200",
                    "bg-amber-200",
                    "bg-green-200",
                    "bg-cyan-200",
                    "bg-blue-200",
                    "bg-indigo-200",
                    "bg-violet-200",
                ];
                bgClass = colors[(lane - 1) % 8];
                break;
            default:
                bgClass = "bg-gray-200";
                break;
        }

        return { bgClass, textClass, borderClass };
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Settings */}
                <Card title="跑道設定">
                    <div className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                跑道規格
                            </label>
                            <div className="flex gap-2">
                                {[TrackBase.M400, TrackBase.M300, TrackBase.M200].map((base) => (
                                    <button
                                        key={base}
                                        onClick={() => setTrackBase(base)}
                                        className={`flex-1 py-2 px-1 sm:px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all
                           ${
                               trackBase === base
                                   ? "bg-gray-800 text-white shadow-md"
                                   : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                           }`}
                                    >
                                        {base}m <span className="hidden sm:inline">標準場</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                跑道風格
                            </label>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                {THEME_PRESETS.map((preset) => (
                                    <button
                                        key={preset.id}
                                        onClick={() => setTrackTheme(preset.id)}
                                        className={`relative overflow-hidden group flex items-center justify-center py-2 px-3 rounded-lg border transition-all ${
                                            trackTheme === preset.id
                                                ? "border-gray-600 bg-gray-50 shadow-inner ring-1 ring-gray-200"
                                                : "border-gray-200 bg-white hover:border-gray-300"
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-1 z-10">
                                            <div
                                                className={`w-6 h-6 rounded-full shadow-sm border border-black/10 ${preset.color}`}
                                            />
                                            <span
                                                className={`text-xs font-medium ${
                                                    trackTheme === preset.id
                                                        ? "text-gray-900"
                                                        : "text-gray-600"
                                                }`}
                                            >
                                                {preset.label}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="border-t border-gray-100 pt-4">
                            <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
                                <button
                                    onClick={() => setCalcMode("PACE")}
                                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${
                                        calcMode === "PACE"
                                            ? "bg-white text-gray-800 shadow-sm"
                                            : "text-gray-500 hover:text-gray-600"
                                    }`}
                                >
                                    <Timer size={16} />
                                    指定配速
                                </button>
                                <button
                                    onClick={() => setCalcMode("DIST_TIME")}
                                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${
                                        calcMode === "DIST_TIME"
                                            ? "bg-white text-gray-800 shadow-sm"
                                            : "text-gray-500 hover:text-gray-600"
                                    }`}
                                >
                                    <Calculator size={16} />
                                    里程推算
                                </button>
                            </div>

                            <div className="min-h-[10rem]">
                                {calcMode === "PACE" ? (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                目標配速 (每公里)
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="number"
                                                        value={targetPaceMin}
                                                        onChange={(e) =>
                                                            setTargetPaceMin(
                                                                Math.max(
                                                                    0,
                                                                    parseInt(e.target.value) || 0
                                                                )
                                                            )
                                                        }
                                                        className="w-full pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="absolute right-3 top-2 text-gray-400 text-sm">
                                                        分
                                                    </span>
                                                </div>
                                                <span className="font-bold text-gray-400">:</span>
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="number"
                                                        value={targetPaceSec}
                                                        onChange={(e) =>
                                                            setTargetPaceSec(
                                                                Math.min(
                                                                    59,
                                                                    Math.max(
                                                                        0,
                                                                        parseInt(e.target.value) ||
                                                                            0
                                                                    )
                                                                )
                                                            )
                                                        }
                                                        className="w-full pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="absolute right-3 top-2 text-gray-400 text-sm">
                                                        秒
                                                    </span>
                                                </div>
                                                <span className="text-gray-500 text-sm">/km</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                總里程 (km)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={totalDistKm}
                                                onChange={(e) =>
                                                    setTotalDistKm(
                                                        Math.max(0, parseFloat(e.target.value) || 0)
                                                    )
                                                }
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                完賽時間
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="number"
                                                        placeholder="0"
                                                        value={totalTime.hours}
                                                        onChange={(e) =>
                                                            handleTotalTimeChange(
                                                                "hours",
                                                                e.target.value
                                                            )
                                                        }
                                                        className="w-full pl-3 pr-6 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="absolute right-2 top-2.5 text-xs text-gray-400">
                                                        h
                                                    </span>
                                                </div>
                                                <span className="text-gray-300 font-bold">:</span>
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="number"
                                                        placeholder="0"
                                                        value={totalTime.minutes}
                                                        onChange={(e) =>
                                                            handleTotalTimeChange(
                                                                "minutes",
                                                                e.target.value
                                                            )
                                                        }
                                                        className="w-full pl-3 pr-6 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="absolute right-2 top-2.5 text-xs text-gray-400">
                                                        m
                                                    </span>
                                                </div>
                                                <span className="text-gray-300 font-bold">:</span>
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="number"
                                                        placeholder="0"
                                                        value={totalTime.seconds}
                                                        onChange={(e) =>
                                                            handleTotalTimeChange(
                                                                "seconds",
                                                                e.target.value
                                                            )
                                                        }
                                                        className="w-full pl-3 pr-6 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="absolute right-2 top-2.5 text-xs text-gray-400">
                                                        s
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Quick Info */}
                <div className="grid grid-rows-2 gap-4">
                    {/* Box 1: Target Pace (Used to be Velocity) */}
                    <div className="bg-gradient-to-br from-gray-800 to-gray-700 text-white rounded-xl p-6 shadow-md flex flex-col justify-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <Flag size={80} />
                        </div>
                        <span className="text-gray-300 font-medium text-sm mb-1">
                            目標配速 (Target Pace)
                        </span>
                        <div className="text-4xl font-bold">
                            {formatPace(paceTotalSeconds)}{" "}
                            <span className="text-lg font-normal text-gray-400">/km</span>
                        </div>
                    </div>

                    {/* Box 2: Lap Time */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col justify-center shadow-sm">
                        <span className="text-gray-500 font-medium text-sm mb-1">
                            內圈標準 ({trackBase}m)
                        </span>
                        <div className="text-3xl font-bold text-gray-800">
                            {formatTimeStr(
                                speedMetersPerSecond > 0 ? trackBase / speedMetersPerSecond : 0,
                                true
                            )}
                            <span className="text-sm font-normal text-gray-400 ml-2">每圈</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <RotateCw size={12} />
                            第一跑道基準時間
                        </div>
                    </div>
                </div>
            </div>

            {/* Realistic Track Visual Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">各跑道詳細數據</h3>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Calculator size={14} />
                        模擬視角: 終點直線
                    </span>
                </div>

                <div className="p-0 overflow-x-auto">
                    {/* Track Container */}
                    <div className="min-w-full flex flex-col bg-gray-200 relative">
                        {/* Finish Line Decoration (Vertical Line across lanes) */}
                        <div className="absolute top-0 bottom-0 right-16 sm:right-24 w-0.5 border-r-2 border-dashed border-white/80 z-10 opacity-70"></div>

                        {lanes.map((lane) => {
                            const dist = getLaneDistance(trackBase, lane);
                            const lapSeconds =
                                speedMetersPerSecond > 0 ? dist / speedMetersPerSecond : 0;
                            const baseSeconds =
                                speedMetersPerSecond > 0 ? trackBase / speedMetersPerSecond : 0;
                            const diff = lapSeconds - baseSeconds;
                            const { bgClass, textClass, borderClass } = getLaneStyle(lane);

                            return (
                                <div
                                    key={lane}
                                    className={`relative h-14 ${bgClass} ${borderClass} flex items-center justify-between px-2 sm:px-4 transition-all hover:brightness-105 group`}
                                >
                                    {/* Left: Lane Number */}
                                    <div className="flex items-center gap-2 sm:gap-4 w-auto sm:w-1/4">
                                        <div
                                            className={`w-8 h-8 rounded-full border-2 border-white/50 flex items-center justify-center text-sm font-bold shadow-sm bg-white/40 ${textClass} shrink-0 mr-3`}
                                        >
                                            {lane}
                                        </div>
                                    </div>

                                    {/* Center: Distance */}
                                    <div
                                        className={`text-center w-auto sm:w-1/4 font-mono text-xs sm:text-sm opacity-80 ${textClass}`}
                                    >
                                        {dist.toFixed(1)}m
                                    </div>

                                    {/* Right: Time Data */}
                                    <div
                                        className={`flex items-center justify-end gap-2 sm:gap-6 flex-1 sm:w-1/2 pr-2 sm:pr-12`}
                                    >
                                        <div className="text-right">
                                            <div
                                                className={`font-bold font-mono text-lg sm:text-xl tracking-wide ${textClass}`}
                                            >
                                                {formatTimeStr(lapSeconds, true)}
                                            </div>
                                        </div>
                                        <div
                                            className={`w-12 sm:w-16 text-right text-[10px] sm:text-xs font-mono opacity-60 ${textClass}`}
                                        >
                                            {lane === 1 ? "Base" : `+${diff.toFixed(2)}s`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex gap-2">
                    <RotateCw size={14} className="mt-0.5 text-gray-400" />
                    <p>
                        跑道參數：標準跑道寬度 1.22m。第一跑道長度 {trackBase}m。
                        <br />
                        公式：L = 2π(R + (n-1)w) + 2S。此計算機使用標準 IAAF 規則增加外圈距離。
                    </p>
                </div>
            </div>
        </div>
    );
};
